import { TranslationNS } from '@gokrew/models/constants';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  Copy,
  Mail,
  Lock,
  Link,
  Eye,
  EyeOff,
  Shield,
  RotateCcw,
  Rocket,
  KeyRound,
} from 'lucide-react';
import { Button, IconButton } from './Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './dialog';
import toast from 'react-hot-toast';

export interface InviteCredentials {
  email: string;
  tempPassword: string;
  loginUrl: string;
  firstName?: string;
}

export type CredentialDialogVariant = 'invite' | 'resetPassword';

export interface InviteSuccessDialogProps {
  open: boolean;
  onClose: () => void;
  onResendInvite?: () => Promise<void> | void;
  credentials: InviteCredentials;
  /** Label shown in the welcome message e.g. "Admin", "Employee", "Super Admin" */
  portalLabel?: string;
  /**
   * Controls header text, icon, copy-all text, and security note.
   * Defaults to 'invite'.
   */
  variant?: CredentialDialogVariant;
}

interface CopyButtonProps {
  text: string;
  className?: string;
  iconOnly?: boolean;
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (!navigator.clipboard) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function CopyButton({
  text,
  className = '',
  iconOnly = true,
}: CopyButtonProps) {
  const { t } = useTranslation(TranslationNS.LibsCommon);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error(t('copy_button.failed'));
    }
  }, [text, t]);

  if (iconOnly) {
    return (
      <IconButton
        Icon={copied ? Check : Copy}
        size="sm"
        onClick={handleCopy}
        title={copied ? t('copy_button.copied') : t('copy_button.copy')}
        strokeWidth={copied ? 2.5 : 2}
        className={className}
      />
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      IconPrefix={copied ? Check : Copy}
      className={className}
    >
      {copied ? t('copy_button.copied') : t('copy_button.copy')}
    </Button>
  );
}

// ─── InviteSuccessDialog ──────────────────────────────────────────────────────
export function InviteSuccessDialog({
  open,
  onClose,
  onResendInvite,
  credentials,
  portalLabel = 'Portal',
  variant = 'invite',
}: InviteSuccessDialogProps) {
  const { t } = useTranslation(TranslationNS.LibsCommon);
  const [showPassword, setShowPassword] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  const isReset = variant === 'resetPassword';

  const handleResend = async () => {
    if (!onResendInvite) return;
    setIsResending(true);
    try {
      await onResendInvite();
    } finally {
      setIsResending(false);
    }
  };

  const copyAllTextKey = isReset
    ? 'reset_password_dialog.copy_all_text'
    : credentials.firstName
      ? 'invite_success_dialog.copy_all_text_named'
      : 'invite_success_dialog.copy_all_text_unnamed';

  const allDetailsText = t(copyAllTextKey, {
    firstName: credentials.firstName,
    email: credentials.email,
    tempPassword: credentials.tempPassword,
    loginUrl: credentials.loginUrl,
  });

  const handleCopyAll = async () => {
    const success = await copyToClipboard(allDetailsText);
    if (success) {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2500);
    } else {
      toast.error(t('copy_button.failed'));
    }
  };

  const config = isReset
    ? {
        Icon: KeyRound,
        iconBg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        iconRing: '#eff6ff',
        iconClass: 'text-blue-500',
        title: t('reset_password_dialog.title'),
        subtitle: credentials.firstName
          ? t('reset_password_dialog.subtitle_named', {
              firstName: credentials.firstName,
            })
          : t('reset_password_dialog.subtitle_unnamed'),
        securityHeading: t('reset_password_dialog.security_reminder_heading'),
        securityBody: t('reset_password_dialog.security_reminder_body'),
        copyAllLabel: t('reset_password_dialog.copy_all'),
        copyAllSuccess: t('reset_password_dialog.copy_all_success'),
        doneLabel: t('reset_password_dialog.done'),
      }
    : {
        Icon: Rocket,
        iconBg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
        iconRing: '#f0fdf4',
        iconClass: 'text-emerald-500',
        title: t('invite_success_dialog.title'),
        subtitle: credentials.firstName
          ? t('invite_success_dialog.subtitle_named', {
              firstName: credentials.firstName,
              portalLabel,
            })
          : t('invite_success_dialog.subtitle_unnamed'),
        securityHeading: t('invite_success_dialog.security_reminder_heading'),
        securityBody: t('invite_success_dialog.security_reminder_body'),
        copyAllLabel: t('invite_success_dialog.copy_all'),
        copyAllSuccess: t('invite_success_dialog.copy_all_success'),
        doneLabel: t('invite_success_dialog.done'),
      };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden">
        <div>
          {/* Icon + Header */}
          <DialogHeader className="items-center text-center mb-6">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{
                background: config.iconBg,
                boxShadow: `0 0 0 8px ${config.iconRing}`,
              }}
            >
              <config.Icon
                size={32}
                strokeWidth={1.5}
                className={config.iconClass}
              />
            </div>

            <DialogTitle className="text-xl font-bold text-gray-900 tracking-tight">
              {config.title}
            </DialogTitle>

            <DialogDescription className="text-sm text-gray-500 max-w-xs">
              {config.subtitle}
            </DialogDescription>
          </DialogHeader>

          {/* Credential Card */}
          <div className="rounded-xl border border-gray-100 divide-y divide-gray-100 overflow-hidden bg-gray-50/50 mb-5">
            {/* Email Row */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                <Mail size={16} strokeWidth={1.8} className="text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">
                  {t('credential_card.email_label')}
                </p>
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {credentials.email}
                </p>
              </div>
              <CopyButton text={credentials.email} />
            </div>

            {/* Password Row */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                <Lock size={16} strokeWidth={1.8} className="text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">
                  {t('credential_card.password_label')}
                </p>
                <p className="text-sm font-mono font-semibold text-gray-800 tracking-wider">
                  {showPassword
                    ? credentials.tempPassword
                    : '•'.repeat(credentials.tempPassword.length)}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <IconButton
                  Icon={showPassword ? EyeOff : Eye}
                  size="sm"
                  onClick={() => setShowPassword((p) => !p)}
                  title={
                    showPassword
                      ? t('credential_card.hide_password')
                      : t('credential_card.show_password')
                  }
                  strokeWidth={2}
                />
                <CopyButton text={credentials.tempPassword} />
              </div>
            </div>

            {/* Login URL Row */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                <Link size={16} strokeWidth={1.8} className="text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">
                  {t('credential_card.login_url_label')}
                </p>
                <a
                  href={credentials.loginUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline truncate block"
                >
                  {credentials.loginUrl}
                </a>
              </div>
              <CopyButton text={credentials.loginUrl} />
            </div>
          </div>

          {/* Security Note */}
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-100 mb-5">
            <Shield
              size={16}
              strokeWidth={2}
              className="text-amber-500 flex-shrink-0 mt-0.5"
            />
            <p className="text-xs text-amber-700 leading-relaxed">
              <span className="font-semibold">{config.securityHeading}</span>{' '}
              {config.securityBody}
            </p>
          </div>

          {/* Copy All Button */}
          <Button
            variant={copiedAll ? 'solidPrimary' : 'primary'}
            size="md"
            onClick={handleCopyAll}
            IconPrefix={copiedAll ? Check : Copy}
            className="w-full mb-4"
          >
            {copiedAll ? config.copyAllSuccess : config.copyAllLabel}
          </Button>

          {/* Footer Actions */}
          <div className="flex items-center justify-between">
            {!isReset && onResendInvite ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResend}
                disabled={isResending}
                IconPrefix={RotateCcw}
                className={isResending ? '[&_svg]:animate-spin' : ''}
              >
                {isResending
                  ? t('invite_success_dialog.resending')
                  : t('invite_success_dialog.resend_invite')}
              </Button>
            ) : (
              <span />
            )}

            <Button variant="outline" size="sm" onClick={onClose}>
              {config.doneLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
